import React, { useState, useEffect, useRef } from 'react';
import { db, auth, functions } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import { Link } from 'react-router-dom';
import { SiInstagram, SiTiktok, SiYoutube, SiFacebook } from 'react-icons/si';
import { STokenLogo } from '../components/StreamDisplay';
import AdRewardButton from '../components/AdRewardButton';
import OnboardingModal from '../components/OnboardingModal';
import TermsAgreementModal from '../components/TermsAgreementModal';
import { useSession } from '../contexts/SessionContext';
import { useTheme } from '../contexts/ThemeContext';
import './Dashboard.css';

const PLATFORMS = [
  {
    id:    'instagram',
    label: 'Instagram',
    color: '#e1306c',
    Icon:  SiInstagram,
    url:   'https://www.instagram.com',
  },
  {
    id:    'tiktok',
    label: 'TikTok',
    color: '#ffffff',
    Icon:  SiTiktok,
    url:   'https://www.tiktok.com',
  },
  {
    id:    'youtube',
    label: 'YouTube',
    color: '#ff0000',
    Icon:  SiYoutube,
    url:   'https://www.youtube.com',
  },
  {
    id:    'facebook',
    label: 'Facebook',
    color: '#1877f2',
    Icon:  SiFacebook,
    url:   'https://www.facebook.com',
  },
];

export default function Dashboard() {
  const [userData, setUserData]                         = useState(null);
  const [loading, setLoading]                           = useState(true);
  const [showOnboarding,     setShowOnboarding]         = useState(false);
  const [showTermsAgreement, setShowTermsAgreement]     = useState(false);
  const userRef = useRef(null);

  const { activePlatform, watchPlatform, watchResult, clearResult } = useSession();
  const { theme } = useTheme() ?? {};
  const isLight = theme === 'light';

  useEffect(() => {
    let unsubFirestore = null;
    const timeout = setTimeout(() => setLoading(false), 3000);

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      userRef.current = user;
      if (!user) { clearTimeout(timeout); setLoading(false); return; }

      unsubFirestore = onSnapshot(
        doc(db, 'users', user.uid),
        (snap) => {
          clearTimeout(timeout);
          const data = snap.data();
          setUserData(data);
          setLoading(false);
          if (data && !data.hasAgreedToTerms) {
            setShowTermsAgreement(true);
          } else if (data && !data.hasSeenOnboarding) {
            setShowOnboarding(true);
          }

          // Dev-only: register FCM token for admin notifications
          if (data?.isDev && Capacitor.isNativePlatform()) {
            registerDevFcmToken();
          }
        },
        (e) => { clearTimeout(timeout); console.error('fetchUser:', e); setLoading(false); }
      );
    });
    return () => { unsubAuth(); unsubFirestore?.(); clearTimeout(timeout); };
  }, []);

  async function registerDevFcmToken() {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      let permission = await PushNotifications.checkPermissions();
      if (permission.receive !== 'granted') {
        permission = await PushNotifications.requestPermissions();
      }
      if (permission.receive !== 'granted') return;
      await PushNotifications.register();
      PushNotifications.addListener('registration', async ({ value: token }) => {
        await httpsCallable(functions, 'registerDevToken')({ token });
      });
    } catch (e) {
      console.warn('[DevFCM]', e.message);
    }
  }

  const totalStreams = userData?.totalStreams || 0;
  const dollarValue  = (totalStreams * 0.00166).toFixed(2);

  const activePlatformInfo = activePlatform
    ? PLATFORMS.find(p => p.id === activePlatform.platform)
    : null;

  return (
    <div className="page dashboard">
      {showTermsAgreement && (
        <TermsAgreementModal onDone={() => {
          setShowTermsAgreement(false);
          if (userData && !userData.hasSeenOnboarding) setShowOnboarding(true);
        }} />
      )}
      {!showTermsAgreement && showOnboarding && (
        <OnboardingModal onDone={() => setShowOnboarding(false)} />
      )}
      <div className="grid-bg" aria-hidden="true" />

      {/* Header */}
      <header className="dashboard__header">
        <div className="dashboard__greeting">
          <span className="dashboard__greeting-sub">Welcome back</span>
          <h1 className="dashboard__greeting-name">
            {userData?.username || userData?.displayName?.split(' ')[0] || (userData?.isDev ? 'Dev' : 'Agent')}
          </h1>
        </div>
        <div className="dashboard__header-badge" />
      </header>

      {/* Balance card */}
      <div className="card card--brand dashboard__balance">
        <div className="dashboard__balance-top">
          <span className="section-title">Total Balance</span>
          <span className="dashboard__balance-dollar">${dollarValue}</span>
        </div>
        <div className="dashboard__balance-stream">
          <STokenLogo size={40} />
          <div>
            <div className="dashboard__balance-amount">
              {loading ? '—' : totalStreams.toLocaleString()}
            </div>
            <div className="dashboard__balance-label">Streams</div>
          </div>
        </div>
        <div className="glow-divider" />
        <div className="dashboard__earn-rate">
          <span className="dashboard__earn-rate-label">Earn rate</span>
          <span className="dashboard__earn-rate-value" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            1 <STokenLogo size={14} /> / min watched
          </span>
        </div>
      </div>

      {/* Watch result banner — shown when returning from a platform */}
      {watchResult && (
        <div
          className={`dashboard__watch-result${watchResult.credited > 0 ? ' dashboard__watch-result--win' : ' dashboard__watch-result--zero'}`}
          onClick={clearResult}
        >
          <div className="dashboard__watch-result-top">
            <span className="dashboard__watch-result-icon">
              {(() => {
                const plat = PLATFORMS.find(p => p.id === watchResult.platform);
                return plat ? <plat.Icon size={24} color={plat.color} /> : '📺';
              })()}
            </span>
            <div className="dashboard__watch-result-info">
              <div className="dashboard__watch-result-title">
                {watchResult.credited > 0
                  ? `+${watchResult.credited.toLocaleString()} Streams earned!`
                  : watchResult.capReached
                  ? 'Daily cap reached'
                  : 'Session too short'}
              </div>
              <div className="dashboard__watch-result-sub">
                {watchResult.label} · {watchResult.minutes}m watched
                {watchResult.capReached && ' · Daily limit hit'}
              </div>
            </div>
            <button className="dashboard__watch-result-close" aria-label="Dismiss">✕</button>
          </div>
        </div>
      )}

      {/* Platforms — tap to go watch & earn */}
      <div className="dashboard__platforms">
        <div className="section-title">Watch &amp; Earn</div>
        <p className="dashboard__platforms-hint">
          Tap a platform, watch content, come back — we'll credit your Streams automatically.
        </p>

        {/* Active tracking banner */}
        {activePlatform && activePlatformInfo && (
          <div className="dashboard__watching-banner" style={{ '--platform-color': activePlatformInfo.color }}>
            <span className="dashboard__watching-dot" />
            <span>Tracking {activePlatformInfo.label} watch time…</span>
          </div>
        )}

        <div className="dashboard__platforms-grid">
          {PLATFORMS.map(p => {
            const isActive = activePlatform?.platform === p.id;
            return (
              <button
                key={p.id}
                className={`card dashboard__platform-card${isActive ? ' dashboard__platform-card--active' : ''}`}
                style={{ '--platform-color': p.color }}
                onClick={() => watchPlatform(p.id, p.label, p.url)}
                aria-label={`Watch ${p.label}`}
              >
                <p.Icon
                  className="dashboard__platform-icon"
                  size={36}
                  color={p.id === 'tiktok' ? (isLight ? '#010101' : '#ffffff') : p.color}
                />
                <span className="dashboard__platform-label">{p.label}</span>
                {isActive && <span className="dashboard__platform-live">LIVE</span>}
                <div className="dashboard__platform-glow" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Watch Ad — earn bonus Streams */}
      <div className="dashboard__ad-section">
        <div className="section-title">Boost Earnings</div>
        <AdRewardButton />
      </div>

      {/* Quick actions */}
      <div className="dashboard__quick">
        <div className="section-title">Quick Access</div>
        <div className="dashboard__quick-grid">
          <Link to="/games" className="card dashboard__quick-card">
            <div className="dashboard__quick-icon">🎰</div>
            <div className="dashboard__quick-label">Games</div>
          </Link>
          <Link to="/daily-bonus" className="card dashboard__quick-card">
            <div className="dashboard__quick-icon">📅</div>
            <div className="dashboard__quick-label">Daily</div>
          </Link>
          <Link to="/shop" className="card dashboard__quick-card">
            <div className="dashboard__quick-icon">🛒</div>
            <div className="dashboard__quick-label">Shop</div>
          </Link>
          <Link to="/wallet" className="card dashboard__quick-card">
            <div className="dashboard__quick-icon">💸</div>
            <div className="dashboard__quick-label">Withdraw</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
